import { createAsyncThunk } from '@reduxjs/toolkit';
import { postFloorplanAiReading } from '../services/postFloorplanAiReading.service';

/**
 * The run-wide refusals of a reading: the assistant's door (no permission,
 * no employer picked, the month's allowance spent) and a model that is not
 * configured. Reading the next drawing would be refused the same way.
 */
export const AI_READING_RUN_STOPPING_CODES = Object.freeze([
  'nerobotti_no_permission',
  'nerobotti_no_business',
  'nerobotti_allowance_exhausted',
  'floorplan_reading_unavailable',
]);

export const isAiReadingRunStoppingCode = (code) =>
  AI_READING_RUN_STOPPING_CODES.includes(code);

/**
 * Reads a drawing with the AI and stores the row it answers with, whose
 * `aiReading` the 3D view and the forms offer as guesses where the row says
 * nothing (withGuessedDrawingFields). Every call spends the company's AI
 * allowance; the API refuses once it is spent.
 *
 * Given `levelMarks` (findLevelMarks: where each level mark is on the
 * image), the values written on them are read as well, from close-ups, and
 * kept as `aiReading.levelMarks`; with `levelMarksOnly`, only those are read
 * and added to the row's reading.
 *
 * Given `floorLevelArea` (a floor plan's building box, fractions of the
 * image), the storey's level is read from enlarged pieces of it and kept as
 * `aiReading.floorLevel`; with `floorLevelOnly`, only that is read.
 *
 * Rejects with `{ status, code, message }`: the API's code and message where
 * it gave them (`nerobotti_*` at the door, `floorplan_image_*` for the
 * image, `floorplan_reading_*` for the reading), nulls otherwise.
 */
export const readFloorplanWithAi = createAsyncThunk(
  'floorplans/readFloorplanWithAi',
  async (
    {
      projectId,
      floorplanId,
      levelMarks = null,
      levelMarksOnly = false,
      floorLevelArea = null,
      floorLevelOnly = false,
    },
    { rejectWithValue }
  ) => {
    const body = {};
    if (levelMarks && levelMarks.length > 0) {
      body.levelMarks = levelMarks.map(({ x, y, box }) => ({ x, y, box }));
      if (levelMarksOnly) body.levelMarksOnly = true;
    }
    if (floorLevelArea) {
      const { x0, y0, x1, y1 } = floorLevelArea;
      body.floorLevelArea = { x0, y0, x1, y1 };
      if (floorLevelOnly) body.floorLevelOnly = true;
    }
    try {
      return await postFloorplanAiReading(projectId, floorplanId, body);
    } catch (error) {
      const response = error && error.response;
      const data = (response && response.data) || {};
      return rejectWithValue({
        status:
          response && Number.isFinite(response.status) ? response.status : null,
        code: typeof data.code === 'string' ? data.code : null,
        message:
          typeof data.message === 'string'
            ? data.message
            : (error && error.message) || null,
      });
    }
  }
);
